<template>
  <login-modal
    v-if="!authStore.isLoggedIn"
    :modal-hash="LOGIN_MODAL_HASH"
    @loginSuccess="handleLoginSuccess"
  >
    <translate tag="a" class="write-a-review"> Write a review</translate>
  </login-modal>
  <translate
    v-else
    v-if="hasTokenForCampsite"
    tag="a"
    @click.prevent="handleWriteReviewClick"
    class="write-a-review direct-link"
  >
    Write a review</translate
  >
</template>
<script setup lang="ts">
interface Props {
  campsiteSlug: string
  campsiteName: string
}

const props = defineProps<Props>()

const LOGIN_MODAL_HASH = 'login-form'
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { $puLocalePath } = useNuxtApp()
const reviewsStore = useReviewsStore()
reviewsStore.clearRequestedCampsiteReview()
const { pendingReviews } = storeToRefs(reviewsStore)

const hasTokenForCampsite = computed(() =>
  pendingReviews.value.some(
    (review) => review.campsiteSlug === props.campsiteSlug,
  ),
)

async function handleLoginSuccess() {
  await reviewsStore.refreshPendingReviews()
  // wait for the login form to be closed and the route (hash) to be updated
  if (route.hash && route.hash.includes(LOGIN_MODAL_HASH)) {
    await new Promise((resolve) => {
      const stop = watch(
        () => route.hash,
        (hash) => {
          if (!hash || !hash.includes(LOGIN_MODAL_HASH)) {
            stop()
            resolve(null)
          }
        },
      )
    })
  }
  await handleWriteReviewClick()
}

async function handleWriteReviewClick() {
  const currentCampsitePendingReviews = pendingReviews.value.filter(
    (review) => review.campsiteSlug === props.campsiteSlug,
  )
  let url: string
  if (currentCampsitePendingReviews.length === 1) {
    const [reviewForCampsite] = currentCampsitePendingReviews
    url = $puLocalePath({
      name: 'reviews-post-review-campsite-campsiteSlug',
      params: {
        campsiteSlug: props.campsiteSlug,
      },
      query: {
        verify: reviewForCampsite.token,
      },
    })
  } else {
    // Other scenarios - redirect to account reviews
    url = $puLocalePath({ name: 'account-reviews' })
    reviewsStore.setRequestedCampsiteReview(
      props.campsiteSlug,
      props.campsiteName,
    )
  }
  await router.push(url)
}
</script>
<style scoped>
.write-a-review {
  align-items: center;
  background-color: #f0f1f3;
  border-radius: var(--border-radius);
  color: var(--colour-dark-blue);
  display: flex;
  justify-content: center;
  padding: 0.5em 1em;
  text-decoration: none;
  font-size: 0.85em;
  font-weight: 500;
  height: 100%;
  line-height: 1;
  text-align: center;
}

.write-a-review:active {
  background: #ced6da;
}

.reviews-filters-wrapper {
  flex: 1 0 auto;
}

.direct-link {
  flex: 0 1 auto;
  height: auto;
}
</style>
